// languagetools:disable
'use client'

import { useApolloClient, useQuery } from '@apollo/client'
import {
  ChatContextProvider,
  ChatProvider as PackageChatProvider,
  ChatRuntimeConfigProvider,
  type IChatTelemetryBreadcrumb,
  type IChatTelemetryContext,
  useChat
} from '@chain-zen/chat'
import * as Sentry from '@sentry/browser'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { DirectChatDetails } from '../components/chat-detail-adapters/direct-chat-details'
import { OfferChatDetails } from '../components/chat-detail-adapters/offer-chat-details'
import { ShipmentChatDetails } from '../components/chat-detail-adapters/shipment-chat-details'
import { ARCHIVE_ALL_CHATS } from '../data/mutations/inbox'
import { CREATE_CHAT_USER, CREATE_USER } from '../data/mutations/user'
import { DOES_USER_EXIST, GET_WORKSPACE_MEMBERS } from '../data/queries/members'
import { useAuth } from '../hooks/use-auth'
import { useBrowserNotification } from '../hooks/use-browser-notification'
import { useUserQuery } from '../hooks/use-user-query'
import { envConfig } from '../lib/env-config'
import type { IUserExistsReturnData } from '../types/user'
import { CreatedVia, MembershipStatus } from '../types/user'
import { isLightThemeRoute } from '../utils/light-theme-routes'
import { TOKEN_UPDATED_EVENT, tokenUtils } from '../utils/token-utils'
import { useAnalytics } from './analytics-context'

interface ChatProviderProps {
  children: React.ReactNode
}

const syncUserChatToken = () => tokenUtils.getToken('User-Chat-Token')
const ACTIVE_CHAT_KEY = 'activeChatID'

const getPathnameFromStorageEventUrl = (url: string | null): string => {
  if (!url) return ''

  try {
    return new URL(url).pathname
  } catch {
    return ''
  }
}

const resolveInboxViewFromPathname = (pathname: string) => {
  if (pathname.includes('/shipment-chats')) return 'shipment' as const
  if (pathname.includes('/team-member-chats')) return 'team-members' as const
  if (pathname.includes('/direct-messages')) return 'direct' as const
  return 'all' as const
}

const isInboxPath = (pathname: string) => pathname === '/user/inbox' || pathname.startsWith('/user/inbox/')

/**
 * Check if localStorage is available and writable.
 * Returns false in private/incognito browsing mode or when localStorage is disabled.
 */
const isLocalStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const testKey = '__localStorage_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

const getActiveChatIDFromStorage = (): string | null => {
  if (!isLocalStorageAvailable()) return null
  try {
    return localStorage.getItem(ACTIVE_CHAT_KEY) || null
  } catch {
    return null
  }
}

const setActiveChatIDInStorage = (chatID: string | null): void => {
  if (!isLocalStorageAvailable()) return
  try {
    localStorage.setItem(ACTIVE_CHAT_KEY, chatID || '')
  } catch {
    // Silently fail in private/incognito mode or when localStorage quota exceeded
  }
}

const clearActiveChatIDInStorage = (): void => {
  if (!isLocalStorageAvailable()) return
  try {
    localStorage.removeItem(ACTIVE_CHAT_KEY)
  } catch {
    // Silently fail in private/incognito mode
  }
}

/**
 * Subscribe to cross-tab active chat ID changes via storage events.
 * This allows syncing the active chat state across browser tabs/windows.
 * Returns an unsubscribe function.
 */
const subscribeToActiveChatIDChanges = (
  listener: (chatID: string | null, sourcePathname: string | null) => void
): (() => void) => {
  if (typeof window === 'undefined' || !isLocalStorageAvailable()) {
    return () => {}
  }

  const handleStorageChange = (event: StorageEvent): void => {
    // Ignore storage events from other keys
    if (event.key !== ACTIVE_CHAT_KEY) return

    // Ignore events that don't come from the storage API
    if (!event.storageArea || event.storageArea !== localStorage) return

    listener(event.newValue, getPathnameFromStorageEventUrl(event.url))
  }

  // Use a passive listener for better performance
  // Storage events are only fired from other tabs/windows, not the current one
  window.addEventListener('storage', handleStorageChange, { passive: true })

  return (): void => {
    window.removeEventListener('storage', handleStorageChange)
  }
}

const ChatProvider = ({ children }: ChatProviderProps) => {
  const client = useApolloClient()
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { create } = useBrowserNotification()
  const { captureEvent } = useAnalytics()
  const { selectedWorkspace, workspaces } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const { myDetails } = useUserQuery()
  const [userChatToken, setUserChatToken] = useState<string | null>(() => syncUserChatToken())

  const { data: workspaceMembersData, loading: isLoadingMembers } = useQuery(GET_WORKSPACE_MEMBERS, {
    context: { headers: { 'ws-id': workspaceID } },
    skip: !workspaceID,
    variables: { input: { membershipStatuses: [MembershipStatus.ACTIVE] } }
  })
  const teamMembers = workspaceMembersData?.workspaceMembers || []
  const mentionableUsers = useMemo(
    () =>
      teamMembers
        .map(
          (member: { user?: { email?: string; id: string; name?: string; phone?: string; photo?: string } | null }) => {
            if (!member.user) return null

            return {
              email: member.user.email || '',
              id: member.user.id,
              image: member.user.photo || '',
              name: member.user.name || '',
              phone: member.user.phone || ''
            }
          }
        )
        .filter(Boolean),
    [teamMembers]
  )

  useEffect(() => {
    const handleTokenUpdated = (event: Event) => {
      const tokenEvent = event as CustomEvent<{ tokenType?: string }>
      if (!tokenEvent.detail?.tokenType || tokenEvent.detail.tokenType === 'User-Chat-Token') {
        setUserChatToken(syncUserChatToken())
      }
    }

    window.addEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated as EventListener)
    return () => {
      window.removeEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated as EventListener)
    }
  }, [])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isInboxPath(pathname)) {
        clearActiveChatIDInStorage()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [pathname])

  const config = useMemo(
    () => ({
      activeChannelState: {
        clear: clearActiveChatIDInStorage,
        get: getActiveChatIDFromStorage,
        set: setActiveChatIDInStorage,
        subscribe: subscribeToActiveChatIDChanges
      },
      archiveAllChats: async () => {
        await client.mutate({ mutation: ARCHIVE_ALL_CHATS })
      },
      captureAnalyticsEvent: (
        eventName: string,
        payload?: Record<string, boolean | null | number | string | undefined>
      ) => {
        if (!payload) {
          captureEvent(eventName)
          return
        }

        const normalizedPayload = Object.fromEntries(
          Object.entries(payload).filter(([, value]) => value !== undefined)
        ) as Record<string, boolean | null | number | string>

        captureEvent(eventName, normalizedPayload)
      },
      createNotification: create,
      currentUser: myDetails?.id
        ? {
            countryCode: myDetails.countryCode || null,
            email: myDetails.email || null,
            id: myDetails.id,
            name: myDetails.name || null,
            phone: myDetails.phone || null,
            photo: myDetails.photo || null,
            username: myDetails.username || null
          }
        : null,
      doesUserExist: async (emailOrPhone: string) => {
        const result = await client.query<{ userExists: IUserExistsReturnData | null }>({
          fetchPolicy: 'network-only',
          query: DOES_USER_EXIST,
          variables: { emailOrPhone }
        })
        return result.data?.userExists || null
      },
      inviteUser: async (emailOrPhone: string) => {
        const createUserResult = await client.mutate<{ createUser: { id: string } }>({
          mutation: CREATE_USER,
          variables: {
            createdVia: CreatedVia.ChaineWebApp,
            emailOrPhone,
            userID: uuidv4()
          }
        })

        const createdUserID = createUserResult.data?.createUser.id
        if (!createdUserID) throw new Error('Failed to create user: no ID returned')

        await client.mutate<{ createChatUser: { id: string } }>({
          mutation: CREATE_CHAT_USER,
          variables: {
            email: emailOrPhone.includes('@') ? emailOrPhone : '',
            id: createdUserID,
            phone: emailOrPhone.includes('@') ? '' : emailOrPhone
          }
        })

        return { id: createdUserID }
      },
      getPathname: () => pathname,
      getSearchParam: (key: string) => searchParams.get(key),
      isInboxPath,
      isEmbedRoute: isLightThemeRoute,
      loggedInUserID: tokenUtils.getUserID(),
      mentionableUsers,
      navigateToInboxChat: ({ chatID, chatType }: { chatID: string; chatType: string }) => {
        router.push(`/user/inbox?chatID=${chatID}&chatType=${chatType}`)
      },
      renderDirectChatDetails: DirectChatDetails,
      renderOfferChatDetails: OfferChatDetails,
      renderShipmentChatDetails: ShipmentChatDetails,
      resolveInboxView: resolveInboxViewFromPathname,
      selectedWorkspaceID: workspaceID,
      isLoadingMembers,
      streamAppKey: envConfig.stream.appKey ?? '',
      teamMembers,
      telemetry: {
        addBreadcrumb: (breadcrumb: IChatTelemetryBreadcrumb) => Sentry.addBreadcrumb(breadcrumb),
        captureException: (error: unknown, context?: IChatTelemetryContext) => Sentry.captureException(error, context),
        captureMessage: (message: string, context?: IChatTelemetryContext) => Sentry.captureMessage(message, context)
      },
      tenorApiKey: envConfig.tenorApiKey,
      userChatToken,
      workspaces
    }),
    [
      captureEvent,
      client,
      create,
      isLoadingMembers,
      mentionableUsers,
      myDetails,
      pathname,
      router,
      searchParams,
      teamMembers,
      userChatToken,
      workspaceID,
      workspaces
    ]
  )

  return (
    <ChatRuntimeConfigProvider config={config}>
      <PackageChatProvider>{children}</PackageChatProvider>
    </ChatRuntimeConfigProvider>
  )
}

export { ChatContextProvider, ChatProvider, useChat }
